import { useState, useEffect } from 'react';
import styled from 'styled-components';

const SupportingDocumentPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid rgb(225, 228, 234);
  border-radius: 16px;
  padding: 12px;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;

  .file-label {
    color: rgb(33, 37, 67);
    text-overflow: ellipsis;
    font-size: 16px;
    font-family: Montserrat, sans-serif;
    font-weight: 500;
    line-height: 160%;
    text-align: left;
    align-self: stretch;
    flex-shrink: 0;
    margin-bottom: 5px;
    word-break: break-word;
  }

  .pdf-container {
    width: 100%;
    height: 600px;
    border-radius: 8px;
    overflow: hidden;
    background: #f5f5f5;
    position: relative;
  }

  iframe {
    width: 100%;
    border-radius: 8px;
    border: none;
    background: white;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    object-fit: scale-down;
  }

  a {
    color: #0066cc;
    text-decoration: underline;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    
    &:hover {
      color: #0052a3;
    }
  }

  .error-message {
    color: #d32f2f;
    font-size: 14px;
    font-family: Montserrat, sans-serif;
    padding: 12px;
    background: #ffebee;
    border-radius: 8px;
    width: 100%;
    margin-bottom: 10px;
  }

  .loading-message {
    color: #666;
    font-size: 14px;
    font-family: Montserrat, sans-serif;
    padding: 20px;
    text-align: center;
    background: #f5f5f5;
    border-radius: 8px;
  }

  .download-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: rgb(33, 37, 67);
    color: white;
    border-radius: 8px;
    text-decoration: none;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    font-weight: 500;
    transition: opacity 0.2s;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
      color: white;
    }
  }

  .button-group {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    width: 100%;
  }

  .viewer-tabs {
    display: flex;
    gap: 5px;
    margin-bottom: 10px;
    border-bottom: 1px solid rgb(225, 228, 234);
    padding-bottom: 5px;
  }

  .viewer-tab {
    padding: 8px 16px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    color: #666;
    transition: all 0.2s;

    &:hover {
      color: rgb(33, 37, 67);
    }

    &.active {
      color: rgb(33, 37, 67);
      border-bottom-color: rgb(33, 37, 67);
      font-weight: 600;
    }
  }
`;

function DocumentRenderer({ fileUrl }) {
  const [ pdfMethod, setPdfMethod] = useState('direct'); // 'direct', 'google', 'mozilla'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
  }, [fileUrl]);

  if (!fileUrl) return null;

  const extension = fileUrl.split(".").pop().toLowerCase().split('?')[0];
  const fileName = ` Supporting Document: #${fileUrl.split("/").pop().split('?')[0] }`|| "Supporting Document";

  // Image files
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
    return (
      <SupportingDocumentPreview>
        <div className="file-label">{fileName}</div>
        <img 
          src={fileUrl} 
          alt={fileName}
          onError={(e) => {
            e.target.style.display = 'none';
            setError('Image failed to load');
          }}
        />
        {error && (
          <div className="error-message">
            {error} <a href={fileUrl} download>Download instead</a>
          </div>
        )}
      </SupportingDocumentPreview>
    );
  }

  // PDF files - Multiple viewing methods
  if (["pdf"].includes(extension)) {
    const handleIframeLoad = () => {
      setLoading(false);
    };

    const handleIframeError = () => {
      setLoading(false);
      if (retryCount === 0) {
        setRetryCount(1);
        setPdfMethod('google');
      } else if (retryCount === 1) {
        setRetryCount(2);
        setPdfMethod('mozilla');
      } else {
        setError('Unable to display PDF. Please download or open in a new tab.');
      }
    };

    const getPdfUrl = () => {
      switch (pdfMethod) {
        case 'google':
          return `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
        case 'mozilla':
          return `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(fileUrl)}`;
        case 'direct':
        default:
          // Add cache buster and PDF.js parameters
          const separator = fileUrl.includes('?') ? '&' : '?';
          return `${fileUrl}${separator}t=${Date.now()}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`;
      }
    };

    return (
      <SupportingDocumentPreview>
        <div className="file-label">{fileName}</div>
{/*         
        <div className="viewer-tabs">
          <button 
            className={`viewer-tab ${pdfMethod === 'direct' ? 'active' : ''}`}
            onClick={() => { setPdfMethod('direct'); setRetryCount(0); setLoading(true); }}
          >
            Direct View
          </button>
          <button 
            className={`viewer-tab ${pdfMethod === 'google' ? 'active' : ''}`}
            onClick={() => { setPdfMethod('google'); setRetryCount(1); setLoading(true); }}
          >
            Google Viewer
          </button>
          <button 
            className={`viewer-tab ${pdfMethod === 'mozilla' ? 'active' : ''}`}
            onClick={() => { setPdfMethod('mozilla'); setRetryCount(2); setLoading(true); }}
          >
            Mozilla Viewer
          </button>
        </div> */}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {loading && !error && (
          <div className="loading-message">Loading PDF...</div>
        )}

        {!error && (
          <div className="pdf-container">
            <iframe 
              key={pdfMethod + fileUrl}
              src={getPdfUrl()}
              title={fileName}
              width="100%"
              height="100%"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              style={{ display: loading ? 'none' : 'block' }}
              allow="fullscreen"
            />
          </div>
        )}

   
      </SupportingDocumentPreview>
    );
  }

  // Office documents
  if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(extension)) {
    const officeViewer = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      fileUrl
    )}`;

    return (
      <SupportingDocumentPreview>
        <div className="file-label">{fileName}</div>
        {loading && <div className="loading-message">Loading document...</div>}
        <iframe 
          src={officeViewer} 
          title={fileName} 
          height="600px"
          width="100%"
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false);
            setError('Unable to preview document');
          }}
          style={{ display: loading ? 'none' : 'block' }}
        />
        {error && <div className="error-message">{error}</div>}
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
          Open in New Tab
        </a>
      </SupportingDocumentPreview>
    );
  }

  // Fallback for other file types
  return (
    <SupportingDocumentPreview>
      <div className="file-label">{fileName}</div>
      <div className="button-group">
        <a 
          href={fileUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="download-button"
        >
          📄 Open File
        </a>
        <a 
          href={fileUrl} 
          download={fileName}
          className="download-button"
        >
          ⬇️ Download File
        </a>
      </div>
    </SupportingDocumentPreview>
  );
}

export default DocumentRenderer;